import { ErrorHandler, NgModule } from '@angular/core';
import { NgxAwesomePopupModule, ToastNotificationConfigModule } from '@costlydeveloper/ngx-awesome-popup';
import { environment } from '../environments/environment';
// Material imports
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { NgChartsModule } from 'ng2-charts';
import { NgxEchartsModule } from 'ngx-echarts';
import { MarkdownModule } from 'ngx-markdown';
import { ToastrModule } from 'ngx-toastr';
import { TableModule } from 'primeng/table';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { ActionsMenuIconComponent } from './components/actions-menu/actions-menu-icon/actions-menu-icon.component';
import { ActionsMenuSelectComponent } from './components/actions-menu/actions-menu-select/actions-menu-select.component';
import { ActionAddOrRemoveOwnerComponent } from './components/actions/action-add-or-remove-owner/action-add-or-remove-owner.component';
import { ActionApplicationOwnerComponent } from './components/actions/action-application-owner/action-application-owner.component';
import { ActionDeleteComponent } from './components/actions/action-delete/action-delete.component';
import { ActionSendEmailComponent } from './components/actions/action-send-email/action-send-email.component';
import { ActionSetResourceQuarantineStateComponent } from './components/actions/action-set-resource-quarantine-state/action-set-resource-quarantine-state.component';
import { AddDomainDialogComponent } from './components/add-domain-dialog/add-domain-dialog.component';
import { AddPolicyDialogComponent } from './components/add-policy-dialog/add-policy-dialog.component';
import { AutocompleteApiComponent } from './components/autocomplete-api/autocomplete-api.component';
import { AutocompleteSearchComponent } from './components/autocomplete-search/autocomplete-search.component';
import { ButtonComponent } from './components/button/button.component';
import { CardFieldsComponent } from './components/card-fields/card-fields.component';
import { ComingSoonComponent } from './components/coming-soon/coming-soon.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { CopyButtonComponent } from './components/copy-button/copy-button.component';
import { CategoryAmountComponent } from './components/dashboards/charts/category-amount/category-amount.component';
import { ChartComponent } from './components/dashboards/charts/chart/chart.component';
import { HorizontalBarChartComponent } from './components/dashboards/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { HorizontalSingleBarChartComponent } from './components/dashboards/charts/horizontal-single-bar-chart/horizontal-single-bar-chart.component';
import { LineChartComponent } from './components/dashboards/charts/line-chart/line-chart.component';
import { PieAnimationChartComponent } from './components/dashboards/charts/pie-animation-chart/pie-animation-chart.component';
import { ResourceRiskComponent } from './components/dashboards/charts/resource-risk/resource-risk.component';
import { RiskMappingScoreComponent } from './components/dashboards/charts/risk-mapping-score/risk-mapping-score.component';
import { SolidGaugeChartComponent } from './components/dashboards/charts/solid-gauge-chart/solid-gauge-chart.component';
import { TableChartComponent } from './components/dashboards/charts/table-chart/table-chart.component';
import { TopBuildersDataChartComponent } from './components/dashboards/charts/top-builders-data-chart/top-builders-data-chart.component';
import { ViolationCountComponent } from './components/dashboards/charts/violation-count/violation-count.component';
import { DashboardCardComponent } from './components/dashboards/dashboard-card/dashboard-card.component';
import { DashboardFiltersComponent } from './components/dashboards/dashboard-filters/dashboard-filters.component';
import { DashboardNewOrgComponent } from './components/dashboards/dashboard-new-org/dashboard-new-org.component';
import { VisibilityDashboardContentComponent } from './components/dashboards/dashboards-content/visibility-dashboard-content/visibility-dashboard-content.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { DescriptedToggleSectionComponent } from './components/descripted-toggle-section/descripted-toggle-section.component';
import { DetailKeyValueComponent } from './components/detail-keyvalue/detail-keyvalue.component';
import { DuplicatePolicyDialogComponent } from './components/duplicate-policy-dialog/duplicate-policy-dialog.component';
import { EditViolationRuleDialogComponent } from './components/edit-violation-rule-dialog/edit-violation-rule-dialog.component';
import { FilterChipsComponent } from './components/filter-chips/filter-chips.component';
import { FormFooterComponent } from './components/form-footer/form-footer.component';
import { GeneralSettingsComponent } from './components/general-settings/general-settings.component';
import { GetResourceDefinitionActionComponent } from './components/get-resource-definition-action/get-resource-definition-action.component';
import { HygieneFiltersComponent } from './components/hygiene-filters/hygiene-filters.component';
import { HygieneVerticalFiltersComponent } from './components/hygiene-vertical-filters/hygiene-vertical-filters.component';
import { InactiveIntegrationDialogComponent } from './components/inactive-integration-dialog/inactive-integration-dialog.component';
import { InfoCardComponent } from './components/info-card/info-card.component';
import { InputWithOperationsComponent } from './components/input-with-operations/input-with-operations.component';
import { IntegrationCreatedComponent } from './components/integration-created/integration-created.component';
import { IntegrationAuthMethodComponent } from './components/integration/integration-auth-method/integration-auth-method.component';
import { IntegrationCredentialsComponent } from './components/integration/integration-credentials/integration-credentials.component';
import { IntegrationEnvironmentComponent } from './components/integration/integration-environment/integration-environment.component';
import { IntegrationPlatformSelectionComponent } from './components/integration/integration-platform-selection/integration-platform-selection.component';
import { InventoryExportComponent } from './components/inventory-export/inventory-export.component';
import { InventoryFiltersComponent } from './components/inventory-filters/inventory-filters.component';
import { InventoryVerticalFiltersComponent } from './components/inventory-vertical-filters/inventory-vertical-filters.component';
import { InviteUserDialogComponent } from './components/invite-user-dialog/invite-user-dialog.component';
import { MenuComponent } from './components/menu/menu.component';
import { MultiSelectV2Component } from './components/multi-select-v2/multi-select-v2.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { NavMenuItemComponent } from './components/nav-menu-item/nav-menu-item.component';
import { NoDataComponent } from './components/no-data/no-data.component';
import { NotApplicableLabelComponent } from './components/not-applicable-label/not-applicable-label.component';
import { ChangeEventJustificationDialogComponent } from './components/note-dialog/change-event-justification-dialog.component';
import { NumericFieldComponent } from './components/numeric-field/numeric-field.component';
import { OnOffToggleComponent } from './components/on-off-toggle/on-off-toggle.component';
import { ExecutionsFiltersComponent } from './components/playbooks/executions-filters/executions-filters.component';
import { ExecutionsListComponent } from './components/playbooks/executions-list/executions-list.component';
import { AddOwnerActionComponent } from './components/playbooks/playbook-action/actions/add-owner-action/add-owner-action.component';
import { AddLabelActionComponent } from './components/playbooks/playbook-action/actions/add-label-action/add-label-action.component';
import { SendEmailActionComponent } from './components/playbooks/playbook-action/actions/send-email-action/send-email-action.component';
import { SendWebhookActionComponent } from './components/playbooks/playbook-action/actions/send-webhook-action/send-webhook-action.component';
import { WaitActionComponent } from './components/playbooks/playbook-action/actions/wait-action/wait-action.component';
import { PlaybookActionComponent } from './components/playbooks/playbook-action/playbook-action.component';
import { PlaybookTemplateCardComponent } from './components/playbooks/playbook-template-card/playbook-template-card.component';
import { PlaybooksFiltersComponent } from './components/playbooks/playbooks-filters/playbooks-filters.component';
import { PlaybooksListComponent } from './components/playbooks/playbooks-list/playbooks-list.component';
import { PlaybooksMarketplaceComponent } from './components/playbooks/playbooks-marketplace/playbooks-marketplace.component';
import { PlaybooksTriggerComponent } from './components/playbooks/playbooks-trigger/playbooks-trigger.component';
import { NewViolationTriggerComponent } from './components/playbooks/playbooks-trigger/triggers/new-violation-trigger/new-violation-trigger.component';
import { PolicyBoxComponent } from './components/policy-box/policy-box.component';
import { PolicyConnectorsComponent } from './components/policy-connectors/policy-connectors.component';
import { PossibleNaCellComponent } from './components/prime-table/cells/possible-na-cell/possible-na-cell.component';
import { ColumnsSelectionComponent } from './components/prime-table/columns-selection/columns-selection.component';
import { PrimeTableComponent } from './components/prime-table/prime-table.component';
import { RelationsGraphComponent } from './components/relations-graph/relations-graph.component';
import { SearchBoxComponent } from './components/search-box/search-box.component';
import { SecurityTagComponent } from './components/security-tag/security-tag.component';
import { SelectV2Component } from './components/select-v2/select-v2.component';
import { SelectComponent } from './components/select/select.component';
import { SelectionButtonComponent } from './components/selection-button/selection-button.component';
import { SelectionButtonsContainerComponent } from './components/selection-buttons-container/selection-buttons-container.component';
import { SeverityLabelComponent } from './components/severity-label/severity-label.component';
import { CarouselCardComponent } from './components/sidepanel-cards/carousel-card/carousel-card.component';
import { RelatedResourceCardComponent } from './components/sidepanel-cards/related-resource-card/related-resource-card.component';
import { RelatedViolationCardComponent } from './components/sidepanel-cards/related-violation-card/related-violation-card.component';
import { SidepanelTabsComponent } from './components/sidepanel-tabs/sidepanel-tabs.component';
import { SinglePageActionsComponent } from './components/single-page-actions/single-page-actions.component';
import { StatusLabelComponent } from './components/status-label/status-label.component';
import { StatusToggleComponent } from './components/status-toggle/status-toggle.component';
import { TableCellArrayComponent } from './components/table-cell-array/table-cell-array.component';
import { TablePaginationComponent } from './components/table-pagination/table-pagination.component';
import { TableRowComponent } from './components/table-row/table-row.component';
import { TableSearchComponent } from './components/table-search/table-search.component';
import { TableSelectFilterComponent } from './components/table-select-filter/table-select-filter.component';
import { TableComponent } from './components/table/table.component';
import { TimelineComponent } from './components/timeline/timeline.component';
import { TimelineIssueComponent } from './components/timeline-issue/timeline-issue.component';
import { TrustedDomainsComponent } from './components/trusted-domains/trusted-domains.component';
import { UserComponent } from './components/user/user.component';
import { VersionService } from './services/infra/version/version.service';
import { ViewFiltersMultiSelectComponent } from './components/view-filters-multi-select/view-filters-multi-select.component';
import { ViolationRulesTableComponent } from './components/violation-rules-table/violation-rules-table.component';
import { ViolationRulesVerticalFiltersComponent } from './components/violation-rules/filters/violation-rules-vertical-filters.component';
import { ViolationRulesComponent } from './components/violation-rules/violation-rules.component';
import { ViolationsExportComponent } from './components/violations-export/violations-export.component';
import { ViolationsFiltersComponent } from './components/violations-filters/violations-filters.component';
import { ViolationsVerticalFiltersComponent } from './components/violations-vertical-filters/violations-vertical-filters.component';
import { WarningDialogComponent } from './components/warning-dialog/warning-dialog.component';
import { InLineControlRulesComponent } from './in-line-control-rules/in-line-control-rules.component';
import { FeatureFlagDirective } from './directives/featureFlag/feature-flag.directive';
import { LongTextTooltipDirective } from './directives/longTextTooltip/long-text-tooltip.directive';
import { OnlyNumbersDirective } from './directives/onlyNumbers/only-numbers.directive';
import { HttpErrorInterceptor } from './interceptors/http-error/http-error.interceptor';
import { HttpPerformanceInterceptor } from './interceptors/http-performance/http-performance.interceptor';
import { HttpRetryInterceptor } from './interceptors/http-retry/http-retry.interceptor';
import { SupportTicketSeveritySelectOptions } from './models/selectOptions/supportTicketSeveritySelectOptions.model';
import { BeautifyDisplayNumberPipe } from './pipes/beautify-display-number.pipe';
import { BeautifyDisplayStringPipe } from './pipes/beautify-display-string/beautify-display-string.pipe';
import { ChartsProviderPipe } from './pipes/charts-data-provider.pipe';
import { DetailKeyValuePipe } from './pipes/detail-key-value/detail-key-value.pipe';
import { DisplayCamelCaseStringPipe } from './pipes/display-camel-case-string/display-camel-case-string.pipe';
import { FormErrorMessagePipe } from './pipes/form-error-message/form-error-message.pipe';
import { RequiredFieldTitlePipe } from './pipes/required-field-title/required-field-title.pipe';
import { SelectOptionByIdPipe } from './pipes/select-option-by-id/select-option-by-id.pipe';
import { SelectOptionTableCellPipe } from './pipes/select-option-table-cell/select-option-table-cell.pipe';
import { SuppressSensitivePipe } from './pipes/suppress-sensitive/suppress-sensitive.pipe';
import { TableCellPipe } from './pipes/table-cell/table-cell.pipe';
import { GlobalErrorHandler } from './services/infra/globalErrorHandler/globalErrorHandler.service';
import { ApiComponent } from './views/api/api/api.component';
import { ChangelogComponent } from './views/changelog/changelog.component';
import { DashboardComponent } from './views/dashboard/dashboard.component';
import { HygieneComponent } from './views/hygiene/hygiene.component';
import { IntegrationComponent } from './views/integration/integration.component';
import { IntegrationsComponent } from './views/integrations/integrations.component';
import { InventoryResourceComponent } from './views/inventory-resource/inventory-resource.component';
import { InventoryComponent } from './views/inventory/inventory.component';
import { LayoutComponent } from './views/layout/layout.component';
import { LoginComponent } from './views/login/login.component';
import { MainDashboardComponent } from './views/main-dashboard/main-dashboard.component';
import { AccountApisComponent } from './views/management/account-apis/account-apis.component';
import { AccountManagementComponent } from './views/management/account-management/account-management.component';
import { UsersComponent } from './views/management/users/users.component';
import { PlaybookComponent } from './views/playbook/playbook.component';
import { PlaybooksComponent } from './views/playbooks/playbooks.component';
import { PoliciesComponent } from './views/policies/policies.component';
import { PolicyConfigComponent } from './views/policy-config/policy-config.component';
import { EnvironmentsComponent } from './views/policy/environments/environments.component';
import { PolicyComponent } from './views/policy/policy.component';
import { SupportComponent } from './views/support/support.component';
import { ViolationComponent } from './views/violation/violation.component';
import { ViolationsComponent } from './views/violations/violations.component';
import { VisibilityDashboardComponent } from './views/visibility-dashboard/visibility-dashboard.component';
import { HyperlinkButtonComponent } from './components/hyperlink-button/hyperlink-button.component';
import { ViewLabelsMultiSelectComponent } from './components/view-labels-multi-select/view-labels-multi-select.component';
import { ViewSelectedLabelComponent } from './components/view-selected-label/view-selected-label.component';
import { LabelsDialogComponent } from './components/labels-dialog/labels-dialog.component';
import { SelectLabelDialogComponent } from './components/select-label-dialog/select-label-dialog.component';
import { PopupTopLineComponent } from './components/popup-top-line/popup-top-line.component';
import { ErrorLoginComponent } from './views/error-login/error-login.component';
import { NewResourceTriggerComponent } from './components/playbooks/playbooks-trigger/triggers/new-resource-trigger/new-resource-trigger.component';
import { ActionSetAccessComponent } from './components/actions/action-set-access/action-set-access.component';
import { PolicyEndpointFilteringComponent } from './components/policy-endpoint-filtering/policy-endpoint-filtering.component';
import { AddEndpointDialogComponent } from './components/add-endpoint-dialog/add-endpoint-dialog.component';
import { CreateLabelDialogComponent } from './components/create-label-dialog/create-label-dialog.component';
import { DeleteLabelDialogComponent } from './components/delete-label-dialog/delete-label-dialog.component';
import { EditLabelDialogComponent } from './components/edit-label-dialog/edit-label-dialog.component';
import { SecureFlowStepsActionComponent } from './components/playbooks/playbook-action/actions/secure-flow-steps-action/secure-flow-steps-action.component';
import { ActionSetCopilotAuthenticationDialogComponent } from './components/actions/powerplatform-actions/set-copilot-authentication/action-set-copilot-authentication-dialog/action-set-copilot-authentication-dialog.component';
import { SetCopilotAuthenticationActionComponent } from './components/playbooks/playbook-action/actions/set-copilot-authentication-action/set-copilot-authentication-action.component';
import { GetFlowRunsActionComponent } from './components/actions/get-flow-runs-action/get-flow-runs-action.component';
import { TagComponent } from './components/tag/tag.component';
import { MultipleTagsComponent } from './components/multiple-tags/multiple-tags.component';
import { RemovePermissionsActionComponent } from './components/playbooks/playbook-action/actions/remove-permissions-action/remove-permissions-action.component';
import { RemoveAccessActionComponent } from './components/playbooks/playbook-action/actions/remove-access-action/remove-access-action.component';
import { RuleTagDisplayNamePipe } from './pipes/rule-tag-display-name/rule-tag-display-name.pipe';
import { EffectivePolicySettingsDialogComponent } from './components/effective-policy-settings-dialog/effective-policy-settings-dialog.component';
import { EffectivePolicySettingsButtonComponent } from './components/effective-policy-settings-button/effective-policy-settings-button.component';
import { EnterpriseAiDashboardComponent } from './views/enterprise-ai/enterprise-ai-dashboard/enterprise-ai-dashboard.component';
import { TopAiPluginsDataChartComponent } from './components/dashboards/charts/top-plugins-data-chart/top-ai-plugins-data-chart.component';
import { TextPopupComponent } from './components/text-popup/text-popup.component';
import { EnterpriseAiDetectionTabsManagementComponent } from './views/enterprise-ai/enterprise-ai-detection-tabs-management/enterprise-ai-detection-tabs-management.component';
import { AppEnterpriseAiInLineControlComponent } from './views/enterprise-ai/app-enterprise-ai-in-line-control/app-enterprise-ai-in-line-control.component';
import { EnterpriseAiActivityComponent } from './views/enterprise-ai/enterprise-ai-activity/enterprise-ai-activity.component';
import { AiActivityVerticalFiltersComponent } from './components/ai-activity-vertical-filters/ai-activity-vertical-filters.component';
import { AiActivityFiltersComponent } from './components/ai-activity-filters/ai-activity-filters.component';
import { AgentStepComponent } from './views/enterprise-ai/enterprise-ai-agent-step/enterprise-ai-agent-step.component';
import { EnterpriseAiFindingsComponent } from './views/enterprise-ai/enterprise-ai-findings/enterprise-ai-findings.component';
import { AiFindingsFiltersComponent } from './components/ai-findings-filters/ai-findings-filters.component';
import { AiFindingsVerticalFiltersComponent } from './components/ai-findings-vertical-filters/ai-findings-vertical-filters.component';
import { EnterpriseAiFindingComponent } from './views/enterprise-ai/enterprise-ai-finding/enterprise-ai-finding.component';
import { PreventionDetailsComponent } from './views/enterprise-ai/enterprise-ai-prevention-details/enterprise-ai-prevention-details.component';
import { EnterpriseAiPreventionMockComponent } from './views/enterprise-ai/enterprise-ai-prevention-mock/enterprise-ai-prevention-mock.component';
import { DateTimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { IssuesComponent } from './views/issues/issues.component';
import { IssueComponent } from './views/issue/issue.component';
import { RiskMappingScoreColumnComponent } from './components/risk-mapping-score-column/risk-mapping-score-column.component';
import { StripHtmlPipe } from './pipes/strip-html/strip-html.pipe';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { MtxLuxonDatetimeModule } from '@ng-matero/extensions-luxon-adapter';
import { AdditionalValuesComponent } from './components/additional-values/additional-values.component';
import { PossibleNaMultiValueCellComponent } from './components/prime-table/cells/possible-na-multi-value-cell/possible-na-multi-value-cell.component';
import { IssueFindingsComponent } from './components/issue-findings/issue-findings.component';
import { GetSensitiveDataActionComponent } from './components/get-sensitive-data-input/get-sensitive-data-input.component';
import { ReplaceUnderscorePipe } from './pipes/replace-underscore/replaceUnderscore.pipe';
import { ResourceTypeDisplayNamePipe } from './pipes/resource-type-display-name-pipe/resource-type-display-name-pipe';
import { PlaybookActionTypeDisplayName } from './pipes/playbook-action-type-display-name-pipe/playbook-action-type-display-name';
import { SupportedResourceTypesTooltipComponent } from './components/playbooks/playbooks-trigger/triggers/supported-resource-types-tooltip/supported-resource-types-tooltip.component';
import { IsolateResourceActionComponent } from './components/playbooks/playbook-action/actions/isolate-resource-action/isolate-resource-action.component';
import { RagCleanupDialogComponent } from './components/rag-cleanup-dialog/rag-cleanup-dialog.component';
import { RagCleanupSummaryDialogComponent } from './components/rag-cleanup-summary-dialog/rag-cleanup-summary-dialog.component';
import { ContactSOCDialogComponent } from './components/contact-soc-dialog/contact-soc-dialog.component';
import { IncidentCreatedSuccessfullyDialogComponent } from './components/incident-created-successfully-dialog/incident-created-successfully-dialog.component';
import { QuarantineUserDialogComponent } from './components/quarantine-user-dialog/quarantine-user-dialog.component';
import { RestrictTriggerDomainsDialogComponent } from './components/restrict-trigger-domains-dialog/restrict-trigger-domains-dialog.component';
import { EvidenceListComponent } from './views/enterprise-ai/components/evidence-list.component';
import { AnchorLinkComponent } from './components/anchor-button/anchor-link.component';
import { SidePanelService } from './features/side-panel/services/side-panel.service';
import { SidePanelComponent } from './features/side-panel/components/side-panel.component';
import { EnterpriseAiDataInsightsComponent } from './views/enterprise-ai/enterprise-ai-data-insights/enterprise-ai-data-insights.component';
import { PrivilegedIdentitiesSectionComponent } from './components/priviliged-identities/privileged-identities-section.component';
import { PrivilegedIdentitiesAiSkillComponent } from './components/priviliged-identities/ai-skill/privileged-identities-ai-skill.component';
import { PrivilegedIdentitiesAgentComponent } from './components/priviliged-identities/agent/privileged-identities-agent.component';
import { PrivilegedIdentitiesGptComponent } from './components/priviliged-identities/gpt/privileged-identities-gpt.component';
import { BadgeComponent } from './components/badge/badge.component';
import { AiDataInsightsVerticalFiltersComponent } from './components/ai-data-insights-vertical-filters/ai-data-insights-vertical-filters.component';
import { ToggleContentComponent } from './components/toggle-content/toggle-content.component';
import { HoverHighlightDirective } from './directives/hover/hover.directive';
import { ActionWithConfirmationTextComponent } from './components/actions/action-with-confirmation-text/action-with-confirmation-text.component';
import { ActionDownloadComponent } from './components/actions/action-download/action-download.component';
import { ToNumberedListPipe } from './pipes/toNumberedList/to-numbered-list.pipe';
import { NewLineCharToHtmlLineBreak } from './pipes/newLineCharToHtmlLineBreak/new-line-char-to-html-line-break.pipe';
import { PolicyExportComponent } from './components/policy-export/policy-export.component';
import { LabelsProviderPipe } from './pipes/labels-provider/labels-provider.pipe';
import { SetActionStatusActionComponent } from './components/playbooks/playbook-action/actions/set-action-status-action/set-action-status-action.component';
import { SetAuthenticationModeComponent } from './components/playbooks/playbook-action/actions/set-authentication-mode/set-authentication-mode.component';
import { SetConfirmationModeComponent } from './components/playbooks/playbook-action/actions/set-confirmation-mode/set-confirmation-mode.component';
import { AuthenticationAndConfirmationModeNamingPipe } from './views/playbook/auth-conf-mode-naming.pipe';
import { AiAgentCitationComponent } from './views/enterprise-ai/enterprise-ai-agent-step/ai-agent-citation.component';
import { BeautifyMcpNamingPipe } from './pipes/beautify-mcp-naming/beautify-mcp-naming.pipe';
import { PlaybookProjectionComponent } from './components/playbooks/playbook-projection/playbook-projection.component';
import { NewPlaybooksTriggerComponent } from './components/playbooks/new-playbooks-trigger/new-playbooks-trigger.component';

@NgModule({
  declarations: [
    AppComponent,
    EvidenceListComponent,
    LoginComponent,
    MenuComponent,
    LayoutComponent,
    NavMenuItemComponent,
    TableComponent,
    TableCellPipe,
    TableSearchComponent,
    SelectComponent,
    DatePickerComponent,
    DateTimePickerComponent,
    TableSelectFilterComponent,
    MultiSelectComponent,
    ChartsProviderPipe,
    StatusToggleComponent,
    TableRowComponent,
    NoDataComponent,
    DetailKeyValueComponent,
    DashboardCardComponent,
    ConfirmationDialogComponent,
    LongTextTooltipDirective,
    SuppressSensitivePipe,
    DetailKeyValuePipe,
    FormErrorMessagePipe,
    DashboardNewOrgComponent,
    SelectOptionByIdPipe,
    ApiComponent,
    UserComponent,
    SeverityLabelComponent,
    StatusLabelComponent,
    TagComponent,
    SidepanelTabsComponent,
    CardFieldsComponent,
    CarouselCardComponent,
    TimelineComponent,
    InventoryComponent,
    InventoryResourceComponent,

    AgentStepComponent,

    PreventionDetailsComponent,
    EnterpriseAiPreventionMockComponent,
    EnterpriseAiDataInsightsComponent,
    MultiSelectV2Component,
    ViolationsFiltersComponent,
    InfoCardComponent,
    IntegrationsComponent,
    IntegrationComponent,
    SelectionButtonComponent,
    TimelineIssueComponent,
    SelectionButtonsContainerComponent,
    RequiredFieldTitlePipe,
    RelatedResourceCardComponent,
    InventoryFiltersComponent,
    RelatedViolationCardComponent,
    FeatureFlagDirective,
    FilterChipsComponent,
    ViolationsComponent,
    ViolationsExportComponent,
    ChangelogComponent,
    SearchBoxComponent,
    ViolationComponent,
    ActionsMenuIconComponent,
    ActionsMenuSelectComponent,
    ActionApplicationOwnerComponent,
    ActionAddOrRemoveOwnerComponent,
    ActionSetAccessComponent,
    ActionSendEmailComponent,
    SinglePageActionsComponent,
    ActionDeleteComponent,
    ComingSoonComponent,
    SecurityTagComponent,
    HygieneComponent,
    HygieneFiltersComponent,
    ChangeEventJustificationDialogComponent,
    PoliciesComponent,
    PolicyBoxComponent,
    PolicyConfigComponent,
    NumericFieldComponent,
    OnOffToggleComponent,
    DescriptedToggleSectionComponent,
    GeneralSettingsComponent,
    TrustedDomainsComponent,
    InventoryExportComponent,
    AutocompleteSearchComponent,
    AddDomainDialogComponent,
    WarningDialogComponent,
    ViolationRulesComponent,
    InLineControlRulesComponent,
    PolicyConnectorsComponent,
    InactiveIntegrationDialogComponent,
    TablePaginationComponent,
    PlaybooksComponent,
    PlaybooksFiltersComponent,
    TableCellArrayComponent,
    PlaybookComponent,
    PlaybooksTriggerComponent,
    NewPlaybooksTriggerComponent,
    NewViolationTriggerComponent,
    PlaybookActionComponent,
    PolicyComponent,
    EditViolationRuleDialogComponent,
    FormFooterComponent,
    AddPolicyDialogComponent,
    DuplicatePolicyDialogComponent,
    EnvironmentsComponent,
    InventoryVerticalFiltersComponent,
    AiActivityVerticalFiltersComponent,
    AiFindingsVerticalFiltersComponent,
    AiDataInsightsVerticalFiltersComponent,
    ViolationsVerticalFiltersComponent,
    HygieneVerticalFiltersComponent,
    AutocompleteApiComponent,
    ViewFiltersMultiSelectComponent,
    SelectV2Component,
    SendEmailActionComponent,
    PlaybooksListComponent,
    ExecutionsListComponent,
    ExecutionsFiltersComponent,
    ActionSetResourceQuarantineStateComponent,
    SendWebhookActionComponent,
    SetCopilotAuthenticationActionComponent,
    PlaybooksMarketplaceComponent,
    PlaybookTemplateCardComponent,
    AccountManagementComponent,
    UsersComponent,
    SelectOptionTableCellPipe,
    InviteUserDialogComponent,
    AccountApisComponent,
    InputWithOperationsComponent,
    AddOwnerActionComponent,
    AddLabelActionComponent,
    RemovePermissionsActionComponent,
    RemoveAccessActionComponent,
    IsolateResourceActionComponent,
    SecureFlowStepsActionComponent,
    SupportComponent,
    MainDashboardComponent,
    EnterpriseAiDashboardComponent,
    EnterpriseAiDetectionTabsManagementComponent,
    AppEnterpriseAiInLineControlComponent,
    EnterpriseAiActivityComponent,
    EnterpriseAiFindingsComponent,
    EnterpriseAiFindingComponent,
    DashboardFiltersComponent,
    RiskMappingScoreComponent,
    RiskMappingScoreColumnComponent,
    ChartComponent,
    LineChartComponent,
    SolidGaugeChartComponent,
    ButtonComponent,
    PieAnimationChartComponent,
    HorizontalSingleBarChartComponent,
    ViolationRulesTableComponent,
    HorizontalBarChartComponent,
    IssuesComponent,
    IssueComponent,
    VisibilityDashboardComponent,
    DashboardComponent,
    VisibilityDashboardContentComponent,
    CategoryAmountComponent,
    TopBuildersDataChartComponent,
    TopAiPluginsDataChartComponent,
    TableChartComponent,
    IntegrationCreatedComponent,
    RelationsGraphComponent,
    PrimeTableComponent,
    ColumnsSelectionComponent,
    NotApplicableLabelComponent,
    PossibleNaCellComponent,
    PossibleNaMultiValueCellComponent,
    ViolationCountComponent,
    WaitActionComponent,
    OnlyNumbersDirective,
    RuleTagDisplayNamePipe,
    ResourceRiskComponent,
    GetResourceDefinitionActionComponent,
    HyperlinkButtonComponent,
    ViewLabelsMultiSelectComponent,
    MultipleTagsComponent,
    ViewSelectedLabelComponent,
    LabelsDialogComponent,
    CreateLabelDialogComponent,
    EditLabelDialogComponent,
    DeleteLabelDialogComponent,
    SelectLabelDialogComponent,
    IntegrationPlatformSelectionComponent,
    IntegrationCredentialsComponent,
    IntegrationAuthMethodComponent,
    IntegrationEnvironmentComponent,
    PopupTopLineComponent,
    IssueFindingsComponent,
    ReplaceUnderscorePipe,
    RagCleanupDialogComponent,
    RagCleanupSummaryDialogComponent,
    ContactSOCDialogComponent,
    IncidentCreatedSuccessfullyDialogComponent,
    QuarantineUserDialogComponent,
    RestrictTriggerDomainsDialogComponent,
    ErrorLoginComponent,
    NewResourceTriggerComponent,
    PolicyEndpointFilteringComponent,
    AddEndpointDialogComponent,
    ActionSetCopilotAuthenticationDialogComponent,
    GetFlowRunsActionComponent,
    EffectivePolicySettingsDialogComponent,
    EffectivePolicySettingsButtonComponent,
    AiActivityFiltersComponent,
    AiFindingsFiltersComponent,
    TextPopupComponent,
    AdditionalValuesComponent,
    TextPopupComponent,
    ResourceTypeDisplayNamePipe,
    PlaybookActionTypeDisplayName,
    SupportedResourceTypesTooltipComponent,
    PrivilegedIdentitiesSectionComponent,
    PrivilegedIdentitiesAgentComponent,
    PrivilegedIdentitiesAiSkillComponent,
    PrivilegedIdentitiesGptComponent,
    ActionWithConfirmationTextComponent,
    ActionDownloadComponent,
  ],
  imports: [
    NgClickOutsideDirective,
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MtxDatetimepickerModule,
    MtxLuxonDatetimeModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatInputModule,
    DragDropModule,
    AuthModule.forRoot({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        audience: environment.auth0.audience,
        redirect_uri: window.location.origin,
        scope: environment.auth0.scope.join(' '),
      },
      errorPath: 'error-login',
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: false,
      httpInterceptor: {
        allowedList: [`${environment.api_base_url}/*`],
      },
      // Below is needed in order auth0 not interfere with OAuth Authorization code flow
      skipRedirectCallback: window.location.pathname === '/app/oauth-authorization-code-redirect',
    }),
    BrowserAnimationsModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    NgxAwesomePopupModule.forRoot({
      colorList: {
        success: '#46cc831a', // optional
        info: '#00A6DA1A', // optional
        warning: '#F983001A', // optional
        danger: '#ef574c1a', // optional
      },
    }),
    ToastNotificationConfigModule.forRoot({
      globalSettings: {
        allowedNotificationsAtOnce: 5,
      },
    }),
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    MarkdownModule.forRoot(),
    NgChartsModule,
    ToastrModule.forRoot(), // ToastrModule added,
    TableModule,
    AnchorLinkComponent,
    BadgeComponent,
    ToggleContentComponent,
    HoverHighlightDirective,
    CopyButtonComponent,
    ToNumberedListPipe,
    NewLineCharToHtmlLineBreak,

    PolicyExportComponent,
    BeautifyDisplayNumberPipe,
    BeautifyDisplayStringPipe,
    StripHtmlPipe,
    LabelsProviderPipe,

    AccordionComponent,
    ViolationRulesVerticalFiltersComponent,
    SetActionStatusActionComponent,
    SetAuthenticationModeComponent,
    SetConfirmationModeComponent,
    PlaybookProjectionComponent,
    AuthenticationAndConfirmationModeNamingPipe,

    GetSensitiveDataActionComponent,
    AiAgentCitationComponent,
    BeautifyMcpNamingPipe,
    DisplayCamelCaseStringPipe,
    SidePanelComponent,
  ],
  exports: [LongTextTooltipDirective],
  providers: [
    MatDatepickerModule,
    MtxDatetimepickerModule,
    LabelsProviderPipe,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpPerformanceInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRetryInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    ChartsProviderPipe,
    RequiredFieldTitlePipe,
    TitleCasePipe,
    SidePanelService,
    VersionService,
    SuppressSensitivePipe,
    BeautifyDisplayStringPipe,
    RuleTagDisplayNamePipe,
    BeautifyDisplayNumberPipe,
    DatePipe,
    DisplayCamelCaseStringPipe,
    SupportTicketSeveritySelectOptions,
    provideHttpClient(withInterceptorsFromDi()),
    ResourceTypeDisplayNamePipe,
    PlaybookActionTypeDisplayName,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
